import { useTenant } from '@modules/tenant/contexts/tenant/TenantContext'
import { documentTitleFor } from '@utils/routeTitles'
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useLayoutEffect,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'

type SetEntityName = (name: string | undefined) => void

const DocumentTitleContext = createContext<SetEntityName>(() => {})

/**
 * The only owner of the document `<title>`, for logged and auth pages alike:
 * React 19 hoists it into `<head>`. The page name comes from `ROUTE_TITLES`;
 * detail layouts replace it with their entity name through
 * `useDocumentTitleEntity`.
 */
const DocumentTitle: FC<PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation()
  const { tenant } = useTenant()
  const [entityName, setEntityName] = useState<string>()

  return (
    <DocumentTitleContext.Provider value={setEntityName}>
      <title>{documentTitleFor(pathname, tenant.name, entityName)}</title>
      {children}
    </DocumentTitleContext.Provider>
  )
}

/**
 * Names the open entity in the document title. Pass `undefined` while it is
 * loading; the name is cleared when the caller unmounts.
 */
const useDocumentTitleEntity = (name?: string) => {
  const setEntityName = useContext(DocumentTitleContext)

  useLayoutEffect(() => {
    setEntityName(name)
    return () => setEntityName(undefined)
  }, [name, setEntityName])
}

export { DocumentTitle, useDocumentTitleEntity }
